// Shared vocabulary for the Visitor & Contractor module — RON-5466.
//
// Values here are the ones the API/DB will use (see `db-schema.md` §5 `tblvisitor_visit`), NOT
// the display strings from the prototype. Keeping the raw enum in state and translating only at
// render time means the screens will not need changing when the real API is plugged in.

// tblvisitor_visit.status
export const VISIT_STATUS = {
  EXPECTED: "expected",
  CHECKED_IN: "checked_in",
  CHECKED_OUT: "checked_out",
  CANCELLED: "cancelled",
  NO_SHOW: "no_show",
};

// tblvisitor_visit.kind
export const VISIT_KIND = {
  VISITOR: "visitor",
  CONTRACTOR: "contractor",
};

// tblvisitor_visit.type
export const VISIT_TYPE = {
  STANDARD: "standard",
  VIP: "vip",
};

// Locale keys, so a status is never concatenated or hardcoded in a screen.
const STATUS_LABEL_KEYS = {
  [VISIT_STATUS.EXPECTED]: "react_visitors_status_expected",
  [VISIT_STATUS.CHECKED_IN]: "react_visitors_status_checked_in",
  [VISIT_STATUS.CHECKED_OUT]: "react_visitors_status_checked_out",
  [VISIT_STATUS.CANCELLED]: "react_visitors_status_cancelled",
  [VISIT_STATUS.NO_SHOW]: "react_visitors_status_no_show",
};

export const getStatusLabelKey = (status) =>
  STATUS_LABEL_KEYS[status] || "react_visitors_status_expected";

export const getKindLabelKey = (kind) =>
  kind === VISIT_KIND.CONTRACTOR
    ? "react_visitors_kind_contractor"
    : "react_visitors_kind_visitor";

// What can be done to a visit.
//
// ── THE ANSWER COMES FROM THE SERVER (RON-6002 §4.5) ─────────────────────────────────────────
//
//   "can_edit and can_cancel are the server's answer. Use them. Do not derive them from status —
//    the rule is status is `expected` AND you hold the capability for that kind of visit, and
//    re-deriving it in three clients is how they drift."
//
// So these predicates read the flags the API sent and do nothing else. The old status-based
// derivation now lives in `VisitorAPI.jsx` where the MOCK SERVER stamps it, which is the same
// place the real server owns it — see `deriveCapabilities` there.
//
//   view   — always allowed, including a cancelled visit (it stays readable, not inert). Not a
//            server flag: §4.5 says "View is always available."
//   edit   — `can_edit`
//   cancel — `can_cancel`
//
// §4.5 also says: **grey the controls out rather than hiding them.**
//
// FAIL CLOSED. A missing flag reads as `false`, never `true`. A dropped field on a bad deploy must
// grey a button out (a bug report) rather than enable one the server will refuse (a confusing 409
// in front of a customer).
//
// Kept as separate predicates rather than one `isVisitLocked` flag because the answers genuinely
// differ per action, and kept as functions rather than inlined property reads so every call site
// stays put — there are ten of them across the list, detail and cancel screens.

export const canViewVisit = () => true;

export const canEditVisit = (visit) => visit?.canEdit === true;

export const canCancelVisit = (visit) => visit?.canCancel === true;

// `cancelAlsoChecksOut` was removed on 23-Sep-2026 (Q5). Cancelling never checks a guest out now:
// §4.8 refuses a cancel once the visitor has arrived, so the case it described cannot happen. A
// guest on site is checked out by reception, not cancelled by their host.

// Contractor vetting never blocks editing: a booking made while vetting is still in flight
// (`isProvisional`) stays editable. Stated here so Phase 4 does not re-introduce a gate.
export const isVettingBlockingEdit = () => false;

// Audit-trail events shown on the View screen. The set mirrors the lifecycle a visit can go
// through; an unknown event falls back to "updated" rather than rendering a raw enum at the user.
// THE API'S OWN ACTION NAMES, confirmed 16-October-2026. They are dotted and verb-first:
// `visit.create`, not `created`.
//
// This map used to hold the undotted past-tense forms, which matched nothing the server sends —
// so every row would have fallen through to the generic "Booking updated" and the trail would
// have read the same line five times. It was never exercised, because the field itself was never
// built: the panel has only ever drawn from our fixtures.
//
// Their note says "map the ones you show and ignore the rest — new ones may appear", which is
// what the fallback is for.
const ACTIVITY_LABEL_KEYS = {
  "visit.create": "react_visitors_activity_created",
  "visit.update": "react_visitors_activity_edited",
  "visit.cancel": "react_visitors_activity_cancelled",
  "visit.checkin": "react_visitors_activity_checked_in",
  "visit.checkout": "react_visitors_activity_checked_out",
  // Reception's own actions. A host seeing "Reception notified" on their visit is the point of
  // the panel — it is the half of the story they were not present for.
  "visit.walkin": "react_visitors_activity_walkin",
  "visit.vip": "react_visitors_activity_vip",
  "visit.notify_host": "react_visitors_activity_notify_host",
  // Added 24-October-2026 (answers-to-frontend-22). The SERVER raises this one, for every email
  // sent to the visitor — the invitation confirmation, the update and the cancellation.
  //
  // It had to be mapped rather than left to the fallback: an unmapped action reads "Booking
  // updated", so an email event would have told the host somebody edited the visit.
  "visit.email": "react_visitors_activity_email_sent",
};

export const activityLabelKey = (event) =>
  ACTIVITY_LABEL_KEYS[event] || "react_visitors_activity_updated";

// ── Contractor vetting (Phase 4) ─────────────────────────────────────────────────
// A contractor is someone the company has vetted, so inviting one is a permission check before it
// is a booking.
//
// THE STATES ARE THE API'S (RON-6002 §4.3). `vetting.state` is one of these five and is carried
// through raw by `visitorApiMapper.js` — the module used to have three of its own
// (cleared/provisional/blocked) and translating between the two sets would have hidden which value
// actually arrived. They map onto the old three like this, which is all the old names meant:
//
//   vetted                      was "cleared"     → books normally
//   started · waiting · review  was "provisional" → books provisionally
//   expired                     was "blocked"     → refused
export const VETTING_STATE = {
  STARTED: "started",
  WAITING: "waiting",
  REVIEW: "review",
  VETTED: "vetted",
  EXPIRED: "expired",
};

// The three in-flight states. Grouped once so no screen has to remember which of the five mean
// "still going through vetting".
const VETTING_IN_FLIGHT = [
  VETTING_STATE.STARTED,
  VETTING_STATE.WAITING,
  VETTING_STATE.REVIEW,
];

const VETTING_LABEL_KEYS = {
  [VETTING_STATE.STARTED]: "react_visitors_vetting_started",
  [VETTING_STATE.WAITING]: "react_visitors_vetting_waiting",
  [VETTING_STATE.REVIEW]: "react_visitors_vetting_review",
  [VETTING_STATE.VETTED]: "react_visitors_vetting_cleared",
  [VETTING_STATE.EXPIRED]: "react_visitors_vetting_expired",
};

export const getVettingLabelKey = (state) =>
  VETTING_LABEL_KEYS[state] || "react_visitors_vetting_unknown";

// Which of the three badge colours a state earns. Returned as a name rather than a class so the
// CSS keeps one rule per colour instead of one per state.
export const getVettingTone = (state) => {
  if (state === VETTING_STATE.VETTED) return "ok";
  if (state === VETTING_STATE.EXPIRED) return "blocked";
  if (VETTING_IN_FLIGHT.includes(state)) return "pending";

  return "unknown";
};

// The lookup's verdict — §4.3, added 29-October-2026. The names are the prototype's own, which is
// why they read the way they do.
//
// Before this, the screen worked the outcome out from `vetting.state` and the dates. The contract
// is explicit that it no longer should: "it returns 0 or 1 contractors AND the verdict, so the
// screen never works it out itself."
//
// `BLOCKED_DATE` needs a `date` on the lookup. Step 1 has none, so in practice it arrives only if
// we ever ask again from step 2 — see `contractorBookingBlock`.
export const LOOKUP_OUTCOME = {
  PROCEED: "proceed",
  PROVISIONAL: "provisional",
  BLOCKED_EXPIRED: "blocked_expired",
  BLOCKED_DATE: "blocked_date",
  BLOCKED_UNKNOWN: "blocked_unknown",
};

// An expired contractor cannot be invited — §5: "expired, or vetted_until before the visit date"
// is the one outcome the server refuses outright, with a 409.
export const isContractorBlocked = (record) =>
  record?.vettingState === VETTING_STATE.EXPIRED;

// In-flight vetting is bookable. The visit comes back flagged `is_provisional` so reception knows
// not to admit them until approval lands, which is the same flag the list already badges.
//
// NOTE: this is a PREVIEW, not the decision. §5 is explicit that the server decides the three-way
// outcome from `contractor_id` alone and answers with `is_provisional` plus a `warning`. This
// predicate exists so the wizard can show the right badge and warning before submitting; the
// authoritative answer is the one that comes back.
export const isContractorProvisional = (record) =>
  VETTING_IN_FLIGHT.includes(record?.vettingState);

// THE DATE GATE. Clearance is granted up to a date, so a contractor who is cleared today can still
// be un-cleared for a visit next month. Compared against the visit's END date, not its start: a
// booking that runs past the clearance is not covered for the whole of it.
//
// No `vettedUntil` means no expiry to check — a provisional record has none, and its own status
// already governs whether it can be booked.
export const isVettingExpiredFor = (record, endDateISO) => {
  if (!record?.vettedUntil || !endDateISO) return false;

  return String(endDateISO) > String(record.vettedUntil);
};

// One place that answers "can this contractor be booked for these dates?", so the lookup step, the
// date step and the review step cannot disagree.
//
// A PREVIEW OF THE SERVER'S ANSWER, NOT THE ANSWER. §5: the server computes the three-way gate and
// returns `is_provisional` + a `warning`, or refuses with a 409. This runs client-side only so the
// host is not walked through three wizard steps to be told no at the end. If the two ever
// disagree, the server wins and the 409 message is what the host sees.
export const contractorBookingBlock = (record, endDateISO, outcome) => {
  if (!record) return "react_visitors_vetting_no_record";

  // THE SERVER'S VERDICT WINS when the lookup gave one (§4.3, 29-October-2026). `outcome` is
  // already the answer to "may this be booked"; re-deriving it from the state would mean two
  // opinions that can disagree, and the server's is the one the write will be judged against.
  const blocked = outcome
    ? outcome === LOOKUP_OUTCOME.BLOCKED_EXPIRED
    : isContractorBlocked(record);

  if (blocked) return "react_visitors_vetting_blocked_message";

  // KEPT, and not a second opinion. Two different reasons:
  //
  // 1. `blocked_date` only comes back when the lookup is given a `date`, and step 1 has none —
  //    the visit dates are chosen on step 2, after the lookup has already happened.
  // 2. `record` is not always a register row. An edit falls back to the visit's own snapshot when
  //    the register cannot be reached, and a snapshot carries no verdict at all. Dropping this
  //    would silently ungate exactly the case the fallback exists for.
  if (isVettingExpiredFor(record, endDateISO)) return "react_visitors_vetting_expired_message";

  return null;
};

// ── Requesting vetting ───────────────────────────────────────────────────────────
//
// WHICH OF THE FOUR THIS IS. All four ask the compliance admin team for the same thing, but
// the host is waiting on four different answers, and the confirmation is the last thing they
// read before they go away and wait.
//
// THE SERVER DECIDES THIS, from the register (§4.10) — we never send it. Until 29-October-2026
// the screen guessed, at click time, from the lookup it had already done; the server settles it
// at write time from the register itself. One answer, and the later one.
//
// `PENDING` IS REAL, and this file argued it was not. The reasoning was that nothing in the
// prototype ever passes it — true, because a contractor mid-vetting can be booked provisionally
// there and never reaches the form. The server has no such shortcut: ask about somebody whose
// vetting is outstanding and `pending` is exactly what comes back.
export const VETTING_REQUEST_REASON = {
  // Not on the register at all.
  UNKNOWN: "unknown",
  // On the register, vetting still outstanding.
  PENDING: "pending",
  // On the register, clearance already lapsed.
  EXPIRED: "expired",
  // Cleared today, but not far enough ahead to cover `needed_by`.
  DATE: "date",
};

const VETTING_REQUESTED_MESSAGE_KEYS = {
  [VETTING_REQUEST_REASON.UNKNOWN]: "react_visitors_vetting_requested_message",
  [VETTING_REQUEST_REASON.PENDING]: "react_visitors_vetting_requested_pending",
  [VETTING_REQUEST_REASON.EXPIRED]: "react_visitors_vetting_requested_expired",
  [VETTING_REQUEST_REASON.DATE]: "react_visitors_vetting_requested_date",
};

// `repeated` means a request for this email was already open and has been UPDATED rather than
// duplicated (§4.10: one request per email, `repeat_count` + 1). "We've asked" would be a small
// lie to a host who is asking a second time because the first went nowhere.
//
// `pending` keeps its own sentence either way: it already says "has been reminded", because a
// contractor whose vetting is outstanding has been asked about by definition.
export const getVettingRequestedMessageKey = (type, repeated = false) => {
  if (repeated && type !== VETTING_REQUEST_REASON.PENDING) {
    return "react_visitors_vetting_requested_again";
  }

  // Falls back to the first-vetting wording for a type we do not recognise. It is the only one
  // of the four that is true whatever the answer was — "we asked them to vet this contractor"
  // is never wrong, where "re-vet" is wrong for somebody who was never vetted.
  return (
    VETTING_REQUESTED_MESSAGE_KEYS[type] ||
    VETTING_REQUESTED_MESSAGE_KEYS[VETTING_REQUEST_REASON.UNKNOWN]
  );
};
// ── Compliance documents ─────────────────────────────────────────────────────────
// A document's expiry is independent of the vetting itself: a contractor can be cleared until next
// June with insurance that lapsed last week, so reception needs both stated separately.
export const DOCUMENT_STATE = {
  VALID: "valid",
  EXPIRING: "expiring",
  EXPIRED: "expired",
  NO_EXPIRY: "no_expiry",
};

// Within this many days of expiry a document is flagged amber rather than green — long enough for
// someone to chase a renewal before the visit.
export const DOCUMENT_EXPIRY_WARNING_DAYS = 30;

export const getDocumentState = (document, todayISO) => {
  if (!document?.expiresOn) return DOCUMENT_STATE.NO_EXPIRY;
  if (!todayISO) return DOCUMENT_STATE.VALID;

  if (String(document.expiresOn) < String(todayISO)) return DOCUMENT_STATE.EXPIRED;

  // Day arithmetic on the ISO strings rather than Date objects, so a timezone cannot shift the
  // boundary by a day — the rest of this module compares ISO dates the same way.
  const days =
    (Date.parse(`${document.expiresOn}T00:00:00Z`) -
      Date.parse(`${todayISO}T00:00:00Z`)) /
    86400000;

  return days <= DOCUMENT_EXPIRY_WARNING_DAYS
    ? DOCUMENT_STATE.EXPIRING
    : DOCUMENT_STATE.VALID;
};

// True when any document in the pack has already lapsed. Surfaced on the review step: it does not
// block the booking (the vetting does that) but reception should not be surprised on the day.
export const hasExpiredDocuments = (record, todayISO) =>
  (record?.documents || []).some(
    (document) => getDocumentState(document, todayISO) === DOCUMENT_STATE.EXPIRED
  );

// Count-sensitive copy.
//
// This app interpolates with `.replace("%s", n)` and has no plural machinery, so "%s bookings
// cancelled" renders as "1 bookings have been cancelled". Any string whose count can legitimately
// be 1 therefore needs an explicit `<key>_one` sibling, and this picks between them.
//
// Kept as one helper rather than a ternary at each call site so a missing `_one` key is a single
// predictable thing to look for.
export const pluralKey = (key, count) => (Number(count) === 1 ? `${key}_one` : key);

export const pluralText = (t, key, count) =>
  t(pluralKey(key, count)).replace("%s", String(count));

// Initials for the avatar chip, e.g. "Sarah Mitchell" -> "SM". Max two letters.
export const getInitials = (name) =>
  String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
